import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { api, ApiError, type CaptureMode } from "../lib/api.js";

/**
 * File hors-ligne des opérations de capture (cahier des charges §7.7).
 *
 * Chaque opération (démarrage de capture, segment audio, suppression du dernier segment,
 * finalisation, annulation) est persistée en IndexedDB **avant** tout envoi réseau, puis rejouée
 * **séquentiellement** : au chargement de l'application et à chaque retour de connexion. Les segments
 * audio (blobs) sont stockés tels quels et survivent à un rechargement ou à la mise en veille.
 *
 * Les captures portent un **identifiant local** généré côté client ; le processeur résout
 * localId → identifiant serveur lors du traitement de l'opération « start », puis l'applique aux
 * opérations suivantes — l'enregistrement fonctionne donc entièrement hors-ligne.
 */

type OpKind = "start" | "segment" | "deleteLast" | "finalize" | "cancel" | "textNote" | "photo";

interface OpRec {
  id?: number;
  kind: OpKind;
  localId: string;
  mode?: CaptureMode;
  title?: string;
  localSegId?: string;
  durationSeconds?: number;
  ext?: string;
  text?: string;
  fileName?: string;
}

interface CaptureRec {
  localId: string;
  mode: CaptureMode;
  title?: string;
  serverId: string | null;
  status: "draft" | "finalizing" | "done" | "cancelled";
  segCount: number;
  createdAt: number;
}

interface BlobRec {
  localSegId: string;
  blob: Blob;
}

interface PensieveDB extends DBSchema {
  captures: { key: string; value: CaptureRec };
  ops: { key: number; value: OpRec; indexes: { byLocal: string } };
  blobs: { key: string; value: BlobRec };
}

let dbPromise: Promise<IDBPDatabase<PensieveDB>> | null = null;
function db(): Promise<IDBPDatabase<PensieveDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PensieveDB>("pensieve-capture", 1, {
      upgrade(database) {
        database.createObjectStore("captures", { keyPath: "localId" });
        const ops = database.createObjectStore("ops", { keyPath: "id", autoIncrement: true });
        ops.createIndex("byLocal", "localId");
        database.createObjectStore("blobs", { keyPath: "localSegId" });
      },
    });
  }
  return dbPromise;
}

function uid(): string {
  return crypto.randomUUID();
}

// --- état observable (bannière hors-ligne / envois en attente) ---
export interface QueueStatus {
  online: boolean;
  pending: number;
}
const subscribers = new Set<() => void>();
let status: QueueStatus = { online: typeof navigator === "undefined" ? true : navigator.onLine, pending: 0 };

export function getQueueStatus(): QueueStatus {
  return status;
}
export function subscribeQueue(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}
function notify(): void {
  for (const cb of subscribers) cb();
}
async function refreshPending(): Promise<void> {
  const d = await db();
  const pending = await d.count("ops");
  if (pending !== status.pending) {
    status = { ...status, pending };
    notify();
  }
}

// --- traitement séquentiel ---
let processing = false;
let retryTimer = 0;

async function execute(d: IDBPDatabase<PensieveDB>, op: OpRec): Promise<void> {
  const cap = await d.get("captures", op.localId);
  switch (op.kind) {
    case "start": {
      const res = await api.startCapture(op.mode ?? "daily", op.title);
      if (cap) await d.put("captures", { ...cap, serverId: res.captureId });
      return;
    }
    case "segment": {
      if (!cap?.serverId) throw new ApiError(0, "Capture serveur inconnue (start non traité).");
      const blobRec = op.localSegId ? await d.get("blobs", op.localSegId) : undefined;
      if (!blobRec) return; // segment déjà supprimé localement : rien à envoyer
      await api.uploadSegment(cap.serverId, blobRec.blob, op.durationSeconds ?? 0, `segment.${op.ext ?? "webm"}`);
      if (op.localSegId) await d.delete("blobs", op.localSegId);
      return;
    }
    case "deleteLast": {
      if (cap?.serverId) await api.deleteLastSegment(cap.serverId);
      return;
    }
    case "finalize": {
      if (cap?.serverId) await api.finalizeCapture(cap.serverId);
      if (cap) await d.put("captures", { ...cap, status: "done" });
      return;
    }
    case "cancel": {
      if (cap?.serverId) await api.deleteCapture(cap.serverId);
      if (cap) await d.put("captures", { ...cap, status: "cancelled" });
      return;
    }
    // Note écrite / photos (note cumulée) : envois indépendants, sans chaîne localId → serveur.
    case "textNote": {
      if (op.text) await api.addTextNote(op.text);
      return;
    }
    case "photo": {
      const blobRec = op.localSegId ? await d.get("blobs", op.localSegId) : undefined;
      if (!blobRec) return; // photo retirée localement avant l'envoi : rien à faire
      await api.addPhoto(blobRec.blob, op.fileName ?? "photo.jpg");
      if (op.localSegId) await d.delete("blobs", op.localSegId);
      return;
    }
  }
}

async function processQueue(): Promise<void> {
  if (processing) return;
  processing = true;
  try {
    const d = await db();
    // boucle séquentielle : une opération à la fois, dans l'ordre d'insertion.
    for (;;) {
      const first = await d.getAll("ops", undefined, 1);
      const op = first[0];
      if (!op) break;
      if (typeof navigator !== "undefined" && !navigator.onLine) break;
      try {
        await execute(d, op);
        if (op.id !== undefined) await d.delete("ops", op.id);
        await refreshPending();
      } catch (e) {
        if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
          // Erreur permanente (requête invalide / introuvable) : on abandonne l'opération.
          if (op.id !== undefined) await d.delete("ops", op.id);
          await refreshPending();
          continue;
        }
        // Erreur transitoire (réseau, 5xx) : on réessaiera plus tard.
        scheduleRetry();
        break;
      }
    }
  } finally {
    processing = false;
  }
}

function scheduleRetry(): void {
  if (retryTimer) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = 0;
    void processQueue();
  }, 5000);
}

/** Démarre l'écoute réseau et lance un premier rejeu (à appeler une fois au montage). */
export function startQueueProcessing(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("online", () => {
    status = { ...status, online: true };
    notify();
    void processQueue();
  });
  window.addEventListener("offline", () => {
    status = { ...status, online: false };
    notify();
  });
  void refreshPending().then(() => processQueue());
}

// --- API utilisée par la machine à états d'enregistrement ---

export async function enqueueStart(mode: CaptureMode, title?: string): Promise<string> {
  const localId = uid();
  const d = await db();
  await d.put("captures", { localId, mode, title, serverId: null, status: "draft", segCount: 0, createdAt: Date.now() });
  await d.add("ops", { kind: "start", localId, mode, title });
  await refreshPending();
  void processQueue();
  return localId;
}

export async function enqueueSegment(localId: string, blob: Blob, durationSeconds: number, ext: string): Promise<void> {
  const localSegId = uid();
  const d = await db();
  await d.put("blobs", { localSegId, blob });
  await d.add("ops", { kind: "segment", localId, localSegId, durationSeconds, ext });
  const cap = await d.get("captures", localId);
  if (cap) await d.put("captures", { ...cap, segCount: cap.segCount + 1 });
  await refreshPending();
  void processQueue();
}

export async function enqueueDeleteLast(localId: string): Promise<void> {
  const d = await db();
  // Si un segment de cette capture est encore en file (non envoyé), on l'annule localement.
  const ops = await d.getAllFromIndex("ops", "byLocal", localId);
  const pendingSegments = ops.filter((o) => o.kind === "segment");
  const last = pendingSegments[pendingSegments.length - 1];
  if (last && last.id !== undefined) {
    await d.delete("ops", last.id);
    if (last.localSegId) await d.delete("blobs", last.localSegId);
  } else {
    await d.add("ops", { kind: "deleteLast", localId });
  }
  const cap = await d.get("captures", localId);
  if (cap) await d.put("captures", { ...cap, segCount: Math.max(0, cap.segCount - 1) });
  await refreshPending();
  void processQueue();
}

export async function enqueueFinalize(localId: string): Promise<void> {
  const d = await db();
  await d.add("ops", { kind: "finalize", localId });
  const cap = await d.get("captures", localId);
  if (cap) await d.put("captures", { ...cap, status: "finalizing" });
  await refreshPending();
  void processQueue();
}

export async function enqueueCancel(localId: string): Promise<void> {
  const d = await db();
  // On purge d'abord toutes les opérations encore en file pour cette capture (avec leurs blobs).
  const ops = await d.getAllFromIndex("ops", "byLocal", localId);
  let startWasPending = false;
  for (const o of ops) {
    if (o.kind === "start") startWasPending = true;
    if (o.localSegId) await d.delete("blobs", o.localSegId);
    if (o.id !== undefined) await d.delete("ops", o.id);
  }
  const cap = await d.get("captures", localId);
  // Si « start » n'a pas encore été envoyé, le serveur ignore tout de cette capture : rien à annuler.
  if (cap?.serverId || (!startWasPending && cap)) {
    await d.add("ops", { kind: "cancel", localId });
  } else if (cap) {
    await d.put("captures", { ...cap, status: "cancelled" });
  }
  await refreshPending();
  void processQueue();
}

/** Note écrite (note cumulée) : mise en file puis envoi indépendant (résiliente hors-ligne). */
export async function enqueueTextNote(text: string): Promise<void> {
  const d = await db();
  await d.add("ops", { kind: "textNote", localId: uid(), text });
  await refreshPending();
  void processQueue();
}

/** Photo (note cumulée) : le fichier est persisté puis envoyé de façon indépendante au retour réseau. */
export async function enqueuePhoto(blob: Blob, fileName: string): Promise<void> {
  const localSegId = uid();
  const d = await db();
  await d.put("blobs", { localSegId, blob });
  await d.add("ops", { kind: "photo", localId: uid(), localSegId, fileName });
  await refreshPending();
  void processQueue();
}

/** Capture en cours non finalisée à restaurer au chargement (mise en veille / rechargement). */
export interface OpenCapture {
  localId: string;
  mode: CaptureMode;
  segCount: number;
}
export async function findOpenCapture(): Promise<OpenCapture | null> {
  const d = await db();
  const all = await d.getAll("captures");
  const drafts = all.filter((c) => c.status === "draft" && c.segCount > 0).sort((a, b) => b.createdAt - a.createdAt);
  const open = drafts[0];
  return open ? { localId: open.localId, mode: open.mode, segCount: open.segCount } : null;
}
